import { QuestionData } from '../../types/questionData';
import { createQuestionDataId, isQuestionDataKey, migrateQuestionDataKey } from '../../utils/storageIdUtils';

export class QuestionDataLocalStorage {
  private static readonly STORAGE_KEY_PREFIX = 'questionData_'; // Keep for backward compatibility checks
  private static readonly SYNC_STATUS_KEY = 'questionData_syncStatus';
  
  /**
   * Create storage key for a question
   * Now uses standardized ID format without prefix
   */
  private static createKey(tenantId: number | string, userId: number | string, testId: string, questionId: string): string {
    return createQuestionDataId(tenantId, userId, testId, questionId);
  }
  
  /**
   * Save question data to localStorage
   */
  static saveQuestionData(data: QuestionData): void {
    let retries = 0;
    const maxRetries = 3;
    
    while (retries < maxRetries) {
      try {
        const key = this.createKey(data.tenantId, data.userId, data.testId, data.questionId);
        
        // Mark as not synced
        const dataToSave = {
          ...data,
          syncedToFirebase: false,
          lastSyncAttempt: Date.now()
        };
        
      
        
     
        localStorage.setItem(key, JSON.stringify(dataToSave));
        
        // Update sync status
        this.updateSyncStatus(key, false);
        
        return; // Success
      } catch (error) {
        if (error instanceof DOMException && error.name === 'QuotaExceededError') {
          // Clear old data before retry
          this.clearOldQuestionData();
          retries++;
          if (retries === maxRetries) {
            console.error('Failed to save after clearing localStorage:', error);
          }
        } else {
          console.error('Error saving to localStorage:', error);
          return;
        }
      }
    }
  }
  
  /**
   * Get question data from localStorage
   */
  static getQuestionData(tenantId: number | string, userId: number | string, testId: string, questionId: string): QuestionData | null {
    try {
      // Try new format first
      const key = this.createKey(tenantId, userId, testId, questionId);
      let stored = localStorage.getItem(key);
      
      // If not found, try old format
      if (!stored) {
        const oldKey = `${this.STORAGE_KEY_PREFIX}${key}`;
        stored = localStorage.getItem(oldKey);
        
        // If found with old key, migrate it
        if (stored) {
       
          localStorage.setItem(key, stored);
          localStorage.removeItem(oldKey);
        }
      }
      
      if (!stored) {
       
        return null;
      }
      
      const parsed = JSON.parse(stored);
     
      
      const data = parsed as QuestionData;
      
      return data;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return null;
    }
  }
  
  /**
   * Get all questions for a test from localStorage
   */
  static getAllQuestionsForTest(tenantId: number | string, userId: number | string, testId: string): Map<string, QuestionData> {
    const questionsMap = new Map<string, QuestionData>();
    const newFormatPrefix = `${tenantId}_${userId}_${testId}_`;
    const oldFormatPrefix = `${this.STORAGE_KEY_PREFIX}${tenantId}_${userId}_${testId}_`;
    
    try {
      // Iterate through all localStorage keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith(newFormatPrefix) || key.startsWith(oldFormatPrefix))) {
          const stored = localStorage.getItem(key);
          if (stored) {
            const data = JSON.parse(stored) as QuestionData;
            questionsMap.set(data.questionId, data);
          }
        }
      }
    } catch (error) {
      console.error('Error reading all questions from localStorage:', error);
    }
    
    return questionsMap;
  }
  
  /**
   * Mark question as synced to Firebase
   */
  static markAsSynced(tenantId: number | string, userId: number | string, testId: string, questionId: string): void {
    try {
      const key = this.createKey(tenantId, userId, testId, questionId);
      const stored = localStorage.getItem(key);
      
      if (stored) {
        const data = JSON.parse(stored) as QuestionData;
        data.syncedToFirebase = true;
        data.syncError = undefined;
        localStorage.setItem(key, JSON.stringify(data));
        
        // Update sync status
        this.updateSyncStatus(key, true);
      }
    } catch (error) {
      console.error('Error marking as synced:', error);
    }
  }
  
  /**
   * Update sync status tracking
   */
  private static updateSyncStatus(key: string, synced: boolean, remove: boolean = false): void {
    try {
      const statusData = this.getSyncStatus();
      
      if (remove) {
        // Remove from both pending and synced
        delete statusData.pending[key];
        delete statusData.synced[key];
      } else if (synced) {
        delete statusData.pending[key];
        statusData.synced[key] = Date.now();
      } else {
        statusData.pending[key] = Date.now();
        delete statusData.synced[key];
      }
      
      localStorage.setItem(this.SYNC_STATUS_KEY, JSON.stringify(statusData));
    } catch (error) {
      console.error('Error updating sync status:', error);
    }
  }
  
  /**
   * Get sync status
   */
  static getSyncStatus(): { pending: Record<string, number>; synced: Record<string, number> } {
    try {
      const stored = localStorage.getItem(this.SYNC_STATUS_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error reading sync status:', error);
    }
    
    return { pending: {}, synced: {} };
  }
  
  /**
   * Get all pending (unsynced) questions
   */
  static getPendingQuestions(): QuestionData[] {
    const pending: QuestionData[] = [];
    const syncStatus = this.getSyncStatus();
    
    try {
      Object.keys(syncStatus.pending).forEach(key => {
        const stored = localStorage.getItem(key);
        if (stored) {
          const data = JSON.parse(stored) as QuestionData;
          if (!data.syncedToFirebase) {
            pending.push(data);
          }
        }
      });
    } catch (error) {
      console.error('Error getting pending questions:', error);
    }
    
    return pending;
  }
  
  /**
   * Clear old synced data (older than 7 days)
   */
  static clearOldSyncedData(daysToKeep: number = 7): void {
    const cutoffTime = Date.now() - (daysToKeep * 24 * 60 * 60 * 1000);
    const syncStatus = this.getSyncStatus();
    
    try {
      Object.entries(syncStatus.synced).forEach(([key, syncTime]) => {
        if (syncTime < cutoffTime) {
          localStorage.removeItem(key);
          delete syncStatus.synced[key];
        }
      });
      
      localStorage.setItem(this.SYNC_STATUS_KEY, JSON.stringify(syncStatus));
    } catch (error) {
      console.error('Error clearing old data:', error);
    }
  }
  
  /**
   * Get localStorage usage info
   */
  static getStorageInfo(): { used: number; count: number; pendingCount: number } {
    let used = 0;
    let count = 0;
    const syncStatus = this.getSyncStatus();
    const pendingCount = Object.keys(syncStatus.pending).length;
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && isQuestionDataKey(key)) {
        count++;
        const value = localStorage.getItem(key);
        if (value) {
          used += key.length + value.length;
        }
      }
    }
    
    return { used, count, pendingCount };
  }
  
  /**
   * Migrate old format keys to new format
   */
  static migrateToNewFormat(): void {
    try {
      // Check if migration has already been done
      const migrationFlag = localStorage.getItem('questionData_migrationComplete_v2');
      if (migrationFlag === 'true') {
        return; // Skip migration if already done
      }
      
      let migratedCount = 0;
      
      // Find all old format keys
      const keysToMigrate: { oldKey: string; newKey: string; data: any }[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.STORAGE_KEY_PREFIX)) {
          const newKey = migrateQuestionDataKey(key);
          if (newKey && newKey !== key) {
            const data = localStorage.getItem(key);
            if (data) {
              keysToMigrate.push({ oldKey: key, newKey, data });
            }
          }
        }
      }
      
      // Migrate each key
      for (const { oldKey, newKey, data } of keysToMigrate) {
        // Check if new key already exists
        if (!localStorage.getItem(newKey)) {
          localStorage.setItem(newKey, data);
          localStorage.removeItem(oldKey);
          migratedCount++;
        } else {
          // If new key exists, remove old key
          localStorage.removeItem(oldKey);
        }
      }
      
      
      // Set migration flag to prevent running again
      localStorage.setItem('questionData_migrationComplete_v2', 'true');
    } catch (error) {
      console.error('Error during migration:', error);
    }
  }

  /**
   * Clear old question data to free up space
   */
  private static clearOldQuestionData(): void {
    try {
      const now = Date.now();
      const threeDaysMs = 3 * 24 * 60 * 60 * 1000; // Changed from 1 day to 3 days
      const keysToRemove: string[] = [];
      
      // Find old data
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && isQuestionDataKey(key)) {
          try {
            const data = localStorage.getItem(key);
            if (data) {
              const parsed = JSON.parse(data);
              // Only remove data older than 3 days that has been synced
              // AND doesn't have optical answers (protect optical answers)
              if (parsed.syncedToFirebase && 
                  parsed.updatedAt && 
                  (now - parsed.updatedAt > threeDaysMs) &&
                  !parsed.opticalChoice) {
                keysToRemove.push(key);
              }
            }
          } catch {
            // If can't parse, it's probably corrupted, but check if it contains optical data
            try {
              const data = localStorage.getItem(key);
              if (data && !data.includes('opticalChoice')) {
                keysToRemove.push(key);
              }
            } catch {
              // Skip if we can't check
            }
          }
        }
      }
      
      // Remove oldest 50% if no old synced data found
      if (keysToRemove.length === 0) {
        const allKeys: { key: string; updatedAt: number }[] = [];
        
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && isQuestionDataKey(key)) {
            try {
              const data = localStorage.getItem(key);
              if (data) {
                const parsed = JSON.parse(data);
                allKeys.push({ key, updatedAt: parsed.updatedAt || 0 });
              }
            } catch {
              allKeys.push({ key, updatedAt: 0 });
            }
          }
        }
        
        // Sort by update time and remove oldest 50%
        allKeys.sort((a, b) => a.updatedAt - b.updatedAt);
        const removeCount = Math.floor(allKeys.length * 0.5);
        for (let i = 0; i < removeCount; i++) {
          keysToRemove.push(allKeys[i].key);
        }
      }
      
      // Remove the keys
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        this.updateSyncStatus(key, false, true); // Remove from sync status
      });
      
    } catch (error) {
      console.error('Error clearing old question data:', error);
    }
  }
}